import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, Clock,
  ExternalLink, Facebook, Globe, Instagram, Loader2, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatInTz, propertyTimezone, tzAbbreviation } from "@/lib/timezone";
import {
  VIEWS, dayNumber, eachDay, formatHour, formatMinutes, formatRangeLabel, isValidYmd,
  rangeForView, shiftAnchor, todayInTz, weekdayHeaders, weekdayShort,
} from "@/lib/calendarDates";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

/**
 * Content Calendar — when each story went live on the website and when
 * it was shared to Facebook / Instagram, plus upcoming scheduled posts.
 *
 * The backend (`GET /api/content-calendar`) returns items already
 * bucketed into the property timezone (`local_date`, `minutes`), so
 * this page never does instant math — it only lays items out. Views:
 * day (hour rail), week (7 columns of chips), month (per-day counts).
 * Clicking any item opens a panel listing everything that went out for
 * that article in the dates on screen; hovering highlights its siblings.
 * View + date live in the URL (?view=week&date=2026-09-07).
 */

const CHANNELS = {
  website: {
    get label() { return tr("Website"); }, Icon: Globe,
    tint: "border-emerald-300 bg-emerald-50 text-emerald-800",
    icon: "text-emerald-700",
  },
  facebook: {
    get label() { return tr("Facebook"); }, Icon: Facebook,
    tint: "border-sky-300 bg-sky-50 text-sky-800",
    icon: "text-sky-700",
  },
  instagram: {
    get label() { return tr("Instagram"); }, Icon: Instagram,
    tint: "border-rose-300 bg-rose-50 text-rose-800",
    icon: "text-rose-700",
  },
};
const CHANNEL_ORDER = ["website", "facebook", "instagram"];
// Instagram already owns the rose tint, so failed posts use a plain
// surface with a strong red border and an explicit "Failed" label.
const FAILED_TINT = "border-red-600 bg-white text-red-700";

const channelOf = (item) => CHANNELS[item.channel] || CHANNELS.website;

function describe(item) {
  return `${channelOf(item).label}${item.variant === "story" ? tr(" Story") : ""}`;
}

function stateLabel(item) {
  if (item.state === "scheduled") return tr("Scheduled");
  if (item.state === "failed") return tr("Failed");
  return tr("Posted");
}

function tooltip(item) {
  return [
    `${formatMinutes(item.minutes)}  ${describe(item)} (${stateLabel(item).toLowerCase()})`,
    item.headline,
    item.target,
    item.error ? `Error: ${item.error}` : "",
  ].filter(Boolean).join("\n");
}

function ringClass(highlight) {
  if (highlight === "selected") return "ring-2 ring-[var(--accent)]";
  if (highlight === "sibling") return "ring-1 ring-[var(--ink)]";
  return "";
}

function groupByDate(items) {
  const map = new Map();
  for (const item of items) {
    if (!map.has(item.local_date)) map.set(item.local_date, []);
    map.get(item.local_date).push(item);
  }
  return map;
}

function StateIcon({ item, className = "w-3 h-3 shrink-0" }) {
  if (item.state === "scheduled") return <Clock className={className} aria-hidden="true" />;
  if (item.state === "failed") return <AlertTriangle className={className} aria-hidden="true" />;
  return null;
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

function interactionProps(item, onSelect, onHover) {
  return {
    type: "button",
    onClick: () => onSelect(item.article_id),
    onMouseEnter: () => onHover(item.article_id),
    onMouseLeave: () => onHover(null),
    onFocus: () => onHover(item.article_id),
    onBlur: () => onHover(null),
    title: tooltip(item),
    "data-testid": `calendar-item-${item.key}`,
  };
}

/** Compact one-line chip for the week view. */
function ItemChip({ item, highlight, onSelect, onHover }) {
  const { Icon } = channelOf(item);
  const tint = item.state === "failed" ? FAILED_TINT : channelOf(item).tint;
  return (
    <button
      {...interactionProps(item, onSelect, onHover)}
      aria-label={`${formatMinutes(item.minutes)}, ${describe(item)}, ${stateLabel(item)}: ${item.headline}`}
      className={[
        "w-full flex flex-col gap-0.5 px-1.5 py-1 border text-left text-[11px] leading-tight",
        tint,
        item.state === "scheduled" ? "border-dashed" : "",
        ringClass(highlight),
      ].join(" ")}
    >
      <span className="flex items-center gap-1 min-w-0">
        <span className="font-mono-display tabular-nums shrink-0">
          {formatMinutes(item.minutes).replace(" ", "")}
        </span>
        <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
        {item.variant === "story" && <span className="shrink-0">{tr("Story")}</span>}
        <StateIcon item={item} />
        {item.state === "failed" && <span className="shrink-0 font-medium">{tr("Failed")}</span>}
      </span>
      <span className="line-clamp-2 text-[var(--ink)]">{item.headline}</span>
    </button>
  );
}

/** Full-width row for the day view. */
function ItemRow({ item, highlight, onSelect, onHover }) {
  const ch = channelOf(item);
  const { Icon } = ch;
  const failed = item.state === "failed";
  return (
    <button
      {...interactionProps(item, onSelect, onHover)}
      className={[
        "w-full flex items-center gap-2 sm:gap-3 px-2 py-1.5 border text-left",
        failed ? FAILED_TINT : "border-[var(--border)] bg-white hover:bg-[var(--secondary)]",
        item.state === "scheduled" ? "border-dashed" : "",
        ringClass(highlight),
      ].join(" ")}
    >
      <span className="w-[3.75rem] sm:w-[4.25rem] shrink-0 font-mono-display text-[11px] tabular-nums text-[var(--ink-muted)]">
        {formatMinutes(item.minutes)}
      </span>
      <span
        className={
          "inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] shrink-0 " +
          (failed ? FAILED_TINT : ch.tint)
        }
      >
        <Icon className="w-3 h-3" aria-hidden="true" />
        <span className="hidden sm:inline">{describe(item)}</span>
        {item.variant === "story" && <span className="sm:hidden">{tr("Story")}</span>}
      </span>
      <span className="flex-1 min-w-0 line-clamp-2 font-serif-display text-sm leading-snug text-[var(--ink)]">
        {item.headline}
      </span>
      <span className="hidden md:inline max-w-[11rem] truncate text-[11px] text-[var(--ink-muted)]">
        {item.target}
      </span>
      {item.state !== "published" && (
        <span className="inline-flex items-center gap-1 text-[10px] shrink-0">
          <StateIcon item={item} />
          <span className="hidden sm:inline">{stateLabel(item)}</span>
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

function DayView({ ymd, items, itemProps }) {
  const dayItems = items.filter((i) => i.local_date === ymd);
  const byHour = new Map();
  for (const item of dayItems) {
    const h = Math.floor(item.minutes / 60);
    if (!byHour.has(h)) byHour.set(h, []);
    byHour.get(h).push(item);
  }
  // Always show the working day; stretch to cover anything earlier/later.
  const busy = [...byHour.keys()];
  const first = Math.min(6, ...busy);
  const last = Math.max(22, ...busy);
  const hours = Array.from({ length: last - first + 1 }, (_, i) => first + i);
  return (
    <div data-testid="calendar-day-view" className="border border-[var(--border)] bg-white">
      {hours.map((h) => (
        <div key={h} className="flex border-b border-[var(--border)] last:border-b-0">
          <div className="w-12 sm:w-16 shrink-0 px-1.5 sm:px-3 py-2 border-r border-[var(--border)] font-mono-display text-[11px] text-[var(--ink-muted)] whitespace-nowrap">
            {formatHour(h)}
          </div>
          <div className="flex-1 min-w-0 min-h-[2.25rem] p-1 sm:px-2 space-y-1">
            {(byHour.get(h) || []).map((item) => (
              <ItemRow key={item.key} item={item} {...itemProps(item)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function WeekView({ days, today, items, onPickDay, itemProps }) {
  const byDate = groupByDate(items);
  return (
    <div data-testid="calendar-week-view" className="border border-[var(--border)] bg-white overflow-x-auto">
      <div className="grid grid-cols-7 min-w-[840px]">
        {days.map((d) => {
          const list = byDate.get(d) || [];
          const isToday = d === today;
          return (
            <div key={d} className="min-w-0 border-r border-[var(--border)] last:border-r-0">
              <button
                type="button"
                onClick={() => onPickDay(d)}
                aria-label={tr("Open {weekdayShort} {dayNumber} in day view", { weekdayShort: weekdayShort(d), dayNumber: dayNumber(d) })}
                className={
                  "w-full flex items-baseline gap-1.5 px-2 py-2 border-b border-[var(--border)] text-left hover:bg-[var(--secondary)] " +
                  (isToday ? "bg-[var(--secondary)]" : "")
                }
              >
                <span className="text-[11px] text-[var(--ink-muted)]">{weekdayShort(d)}</span>
                <span className={"font-serif-display text-lg leading-none " + (isToday ? "text-[var(--accent)]" : "")}>
                  {dayNumber(d)}
                </span>
                {list.length > 0 && (
                  <span className="ml-auto text-[10px] font-mono-display text-[var(--ink-muted)]">
                    {list.length}
                  </span>
                )}
              </button>
              <div className="p-1.5 space-y-1 min-h-[15rem]">
                {list.map((item) => (
                  <ItemChip key={item.key} item={item} {...itemProps(item)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthView({ days, today, monthStart, monthEnd, items, onPickDay }) {
  const counts = useMemo(() => {
    const map = new Map();
    for (const item of items) {
      if (!map.has(item.local_date)) {
        map.set(item.local_date, { website: 0, facebook: 0, instagram: 0, scheduled: 0, failed: 0 });
      }
      const c = map.get(item.local_date);
      if (item.state === "published") c[item.channel] = (c[item.channel] || 0) + 1;
      else c[item.state] += 1;
    }
    return map;
  }, [items]);
  // Website posts per day (published or scheduled), earliest first —
  // the headlines an editor scans for when hunting for a story.
  const stories = useMemo(() => {
    const map = new Map();
    for (const item of items) {
      if (item.channel !== "website" || item.state === "failed") continue;
      if (!map.has(item.local_date)) map.set(item.local_date, []);
      map.get(item.local_date).push(item);
    }
    return map;
  }, [items]);

  return (
    <div data-testid="calendar-month-view" className="border border-[var(--border)] bg-white">
      <div className="grid grid-cols-7 border-b border-[var(--border)]">
        {weekdayHeaders().map((h) => (
          <div key={h} className="px-1.5 sm:px-2 py-1.5 text-[11px] text-[var(--ink-muted)]">{h}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, idx) => {
          const c = counts.get(d);
          const total = c ? c.website + c.facebook + c.instagram + c.scheduled + c.failed : 0;
          const outside = d < monthStart || d > monthEnd;
          const lastCol = (idx + 1) % 7 === 0;
          const lastRow = idx >= days.length - 7;
          return (
            <button
              key={d}
              type="button"
              onClick={() => onPickDay(d)}
              aria-label={tr("{weekdayShort} {d}: {total} item{s}. Open day view", { weekdayShort: weekdayShort(d), d, total, s: total === 1 ? "" : "s" })}
              data-testid={`calendar-month-cell-${d}`}
              className={[
                "min-h-[4.5rem] sm:min-h-[6.5rem] p-1.5 sm:p-2 flex flex-col items-start gap-0.5 text-left hover:bg-[var(--secondary)]",
                "border-[var(--border)]",
                lastCol ? "" : "border-r",
                lastRow ? "" : "border-b",
                outside ? "opacity-50" : "",
              ].join(" ")}
            >
              <span
                className={
                  "text-sm leading-6 " +
                  (d === today ? "inline-flex justify-center w-6 bg-[var(--slate-900)] text-white" : "")
                }
              >
                {dayNumber(d)}
              </span>
              {c && (
                <span className="flex flex-col sm:flex-row sm:flex-wrap sm:gap-x-2 gap-y-0.5">
                  {CHANNEL_ORDER.map((ch) => {
                    if (!c[ch]) return null;
                    const { Icon, icon } = CHANNELS[ch];
                    return (
                      <span key={ch} className="inline-flex items-center gap-1 text-[11px] font-mono-display tabular-nums">
                        <Icon className={"w-3 h-3 " + icon} aria-hidden="true" />
                        {c[ch]}
                      </span>
                    );
                  })}
                  {c.scheduled > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono-display tabular-nums text-[var(--ink-muted)]">
                      <Clock className="w-3 h-3" aria-hidden="true" />
                      {c.scheduled}
                    </span>
                  )}
                  {c.failed > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono-display tabular-nums text-red-700">
                      <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                      {c.failed}
                    </span>
                  )}
                </span>
              )}
              {(stories.get(d) || []).length > 0 && (
                <span className="hidden sm:block w-full mt-0.5 space-y-0.5">
                  {stories.get(d).slice(0, 3).map((s) => (
                    <span key={s.key} className="block truncate text-[11px] leading-tight text-[var(--ink)]">
                      {s.headline}
                    </span>
                  ))}
                  {stories.get(d).length > 3 && (
                    <span className="block text-[10px] text-[var(--ink-muted)]">{tr("+{value} more", { value: stories.get(d).length - 3 })}</span>
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Article detail panel
// ---------------------------------------------------------------------------

function ArticlePanel({ articleId, items, tz, onClose }) {
  const rows = items.filter((i) => i.article_id === articleId);
  if (!articleId || rows.length === 0) return null;
  return (
    <aside
      role="dialog"
      aria-label={tr("Posts for {headline}", { headline: rows[0].headline })}
      data-testid="calendar-article-panel"
      className="fixed z-30 inset-x-0 bottom-0 md:inset-x-auto md:right-6 md:bottom-6 md:w-[26rem] max-h-[65vh] flex flex-col bg-white border border-[var(--border)] shadow-lg"
    >
      <div className="flex items-start gap-3 px-4 py-3 border-b border-[var(--border)]">
        <h2 className="flex-1 font-serif-display text-lg leading-snug">{rows[0].headline}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={tr("Close panel")}
          className="shrink-0 w-7 h-7 inline-flex items-center justify-center text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--secondary)]"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <ul className="flex-1 overflow-y-auto divide-y divide-[var(--border)]">
        {rows.map((item) => {
          const ch = channelOf(item);
          const { Icon } = ch;
          const failed = item.state === "failed";
          return (
            <li key={item.key} className="flex items-start gap-3 px-4 py-2.5">
              <span
                className={
                  "mt-0.5 shrink-0 w-7 h-7 inline-flex items-center justify-center border " +
                  (failed ? FAILED_TINT : ch.tint) +
                  (item.state === "scheduled" ? " border-dashed" : "")
                }
              >
                <Icon className="w-3.5 h-3.5" aria-hidden="true" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  {describe(item)}
                  {item.channel !== "website" && (
                    <span className="text-[var(--ink-muted)]">{" "}{tr("on {target}", { target: item.target })}</span>
                  )}
                </div>
                <div className="text-[11px] font-mono-display text-[var(--ink-muted)]">
                  {formatInTz(item.at, tz, "full")}
                </div>
                {item.state !== "published" && (
                  <div className={"mt-0.5 inline-flex items-center gap-1 text-[11px] " + (failed ? "text-red-700" : "text-[var(--ink-muted)]")}>
                    <StateIcon item={item} />
                    {stateLabel(item)}
                  </div>
                )}
                {item.error && (
                  <div className="mt-0.5 text-[11px] text-red-700 break-words">{item.error}</div>
                )}
              </div>
              {item.permalink && item.state === "published" && (
                <a
                  href={item.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={tr("View the {describe} post", { describe: describe(item) })}
                  title={tr("View post")}
                  className="shrink-0 w-7 h-7 inline-flex items-center justify-center text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--secondary)]"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </li>
          );
        })}
      </ul>
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[var(--border)]">
        {trRich("<0>Posts in the dates on screen</0>", null, [<span className="text-[11px] text-[var(--ink-muted)]" />])}
        <Link
          to={`/article/${articleId}`}
          data-testid="calendar-open-article"
          className="inline-flex items-center px-3 py-1.5 text-sm bg-[var(--slate-900)] text-white hover:opacity-90"
        >{tr("Open article")}</Link>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function defaultView() {
  if (typeof window !== "undefined" && window.matchMedia?.("(max-width: 639px)").matches) {
    return "day";
  }
  return "week";
}

export default function ContentCalendarPage() {
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
    staleTime: 5 * 60 * 1000,
  });
  const tz = propertyTimezone(settings);
  const today = todayInTz(tz);

  const [params, setParams] = useSearchParams();
  const view = VIEWS.includes(params.get("view")) ? params.get("view") : defaultView();
  const anchor = isValidYmd(params.get("date")) ? params.get("date") : today;
  const range = useMemo(() => rangeForView(view, anchor), [view, anchor]);
  const days = useMemo(() => eachDay(range.start, range.end), [range]);

  const go = (nextView, nextDate) => {
    const p = new URLSearchParams(params);
    p.set("view", nextView);
    p.set("date", nextDate);
    setParams(p, { replace: true });
  };

  const { data, isLoading, isError, error, isFetching, isPlaceholderData } = useQuery({
    queryKey: ["content-calendar", range.start, range.end],
    queryFn: () => api.getContentCalendar(range.start, range.end),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const displayTz = data?.timezone || tz;
  const items = useMemo(() => data?.items || [], [data]);

  const [channels, setChannels] = useState({ website: true, facebook: true, instagram: true });
  const visible = useMemo(
    () => items.filter((i) => channels[i.channel] !== false),
    [items, channels],
  );
  const counts = useMemo(() => {
    const c = { website: 0, facebook: 0, instagram: 0 };
    for (const i of items) {
      if (i.local_date >= range.start && i.local_date <= range.end && c[i.channel] !== undefined) {
        c[i.channel] += 1;
      }
    }
    return c;
  }, [items, range]);
  const inRangeCount = counts.website + counts.facebook + counts.instagram;

  const [selectedId, setSelectedId] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setSelectedId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const itemProps = (item) => ({
    highlight:
      item.article_id === selectedId ? "selected"
        : item.article_id === hoverId ? "sibling"
          : null,
    onSelect: setSelectedId,
    onHover: setHoverId,
  });
  const pickDay = (d) => go("day", d);

  return (
    <div className="flex-1 min-w-0 overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-10 py-8 pb-48 md:pb-8">
        <div className="mb-6">
          <div className="overline mb-2">{tr("Newsroom · Calendar")}</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight">{tr("Content Calendar")}</h1>
          <p className="mt-3 text-sm text-[var(--ink-muted)] max-w-2xl">{tr("When each story went live on the website and when it was shared to Facebook and Instagram, including posts scheduled for later. Select a post to see everything that went out for that story.")}</p>
          <p className="mt-2 text-[11px] font-mono-display text-[var(--ink-muted)]">{trRich("All times shown in <0>{displayTz}</0> ({tzAbbreviation})", { displayTz, tzAbbreviation: tzAbbreviation(displayTz) }, [<span className="text-[var(--ink)]" />])}</p>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-3">
          <div role="group" aria-label={tr("Calendar view")} className="inline-flex border border-[var(--border)]">
            {VIEWS.map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                data-testid={`calendar-view-${v}`}
                onClick={() => go(v, anchor)}
                className={
                  "px-3 py-1.5 text-sm capitalize " +
                  (view === v
                    ? "bg-[var(--slate-900)] text-white"
                    : "bg-white text-[var(--ink)] hover:bg-[var(--secondary)]")
                }
              >
                {v}
              </button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => go(view, shiftAnchor(view, anchor, -1))}
              aria-label={tr("Previous {view}", { view })}
              data-testid="calendar-prev"
              className="w-8 h-8 inline-flex items-center justify-center border border-[var(--border)] bg-white hover:bg-[var(--secondary)]"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => go(view, today)}
              data-testid="calendar-today"
              className="h-8 px-3 text-sm border border-[var(--border)] bg-white hover:bg-[var(--secondary)]"
            >{tr("Today")}</button>
            <button
              type="button"
              onClick={() => go(view, shiftAnchor(view, anchor, 1))}
              aria-label={tr("Next {view}", { view })}
              data-testid="calendar-next"
              className="w-8 h-8 inline-flex items-center justify-center border border-[var(--border)] bg-white hover:bg-[var(--secondary)]"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <h2
            aria-live="polite"
            data-testid="calendar-range-label"
            className="font-serif-display text-xl sm:text-2xl tracking-tight"
          >
            {formatRangeLabel(view, anchor)}
          </h2>
          {isFetching && (
            <Loader2 className="w-4 h-4 animate-spin text-[var(--ink-muted)]" aria-label={tr("Loading")} />
          )}
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
          {CHANNEL_ORDER.map((k) => {
            const { label, Icon, tint } = CHANNELS[k];
            const on = channels[k];
            return (
              <label
                key={k}
                data-testid={`calendar-filter-${k}`}
                className={
                  "inline-flex items-center gap-1.5 px-2.5 py-1 border cursor-pointer select-none " +
                  (on ? tint : "border-[var(--border)] bg-white text-[var(--ink-muted)] opacity-60")
                }
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => setChannels((s) => ({ ...s, [k]: !s[k] }))}
                  className="w-3 h-3 accent-current"
                />
                <Icon className="w-3 h-3" aria-hidden="true" />
                <span>{label}</span>
                <span className="opacity-80 tabular-nums">({counts[k]})</span>
              </label>
            );
          })}
          <span className="ml-auto hidden sm:inline-flex items-center gap-3 text-[11px] text-[var(--ink-muted)]">
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" aria-hidden="true" />{" "}{tr("Scheduled")}</span>
            <span className="inline-flex items-center gap-1 text-red-700">
              <AlertTriangle className="w-3 h-3" aria-hidden="true" />{" "}{tr("Failed")}</span>
          </span>
        </div>

        {isError && (
          <div className="mb-4 text-sm px-4 py-3 border border-red-200 bg-red-50 text-red-800">{tr("Couldn’t load the calendar: {detail}. It retries every minute, or reload the page.", { detail: error?.response?.data?.detail || error?.message || tr("unknown error") })}</div>
        )}

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)] py-8">
            <Loader2 className="w-4 h-4 animate-spin" />{" "}{tr("Loading calendar…")}</div>
        ) : (
          <>
            {!isError && !isPlaceholderData && inRangeCount === 0 && (
              <div
                data-testid="calendar-empty"
                className="mb-3 flex items-start gap-2 px-3 py-2 border border-dashed border-[var(--border)] text-sm text-[var(--ink-muted)]"
              >
                <CalendarDays className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                {trRich("<0>Nothing went out on these dates. Facebook and Instagram posts from before the calendar was added may be incomplete.</0>", null, [<span />])}
              </div>
            )}
            {view === "day" && (
              <DayView ymd={anchor} items={visible} itemProps={itemProps} />
            )}
            {view === "week" && (
              <WeekView
                days={days}
                today={today}
                items={visible}
                onPickDay={pickDay}
                itemProps={itemProps}
              />
            )}
            {view === "month" && (
              <MonthView
                days={days}
                today={today}
                monthStart={range.monthStart}
                monthEnd={range.monthEnd}
                items={visible}
                onPickDay={pickDay}
              />
            )}
          </>
        )}
      </div>

      <ArticlePanel
        articleId={selectedId}
        items={items}
        tz={displayTz}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}